class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach(animation => animation.play());
    } else {
      this.animations.forEach(animation => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty('--header-bottom-position-desktop', `${Math.floor(this.header.getBoundingClientRect().bottom)}px`);
  }
}

customElements.define('header-menu', HeaderMenu);

(() => {
  const ADWOA_LOGO = 'https://cdn.shopify.com/s/files/1/0690/6986/5239/files/adwoa-splash-logo.svg?v=1787075191';
  const HOVER_DEGREES_PER_SECOND = 30;

  let activeLogo = null;
  let rotation = 0;
  let hovering = false;
  let hoverFrameId = null;
  let lastHoverFrame = 0;

  const renderLogo = () => {
    if (!activeLogo) return;
    activeLogo.style.transform = `rotate(${rotation}deg)`;
  };

  const hoverFrame = (now) => {
    if (!hovering) {
      hoverFrameId = null;
      lastHoverFrame = 0;
      return;
    }

    if (!lastHoverFrame) lastHoverFrame = now;
    const deltaSeconds = Math.min((now - lastHoverFrame) / 1000, 0.05);
    lastHoverFrame = now;
    rotation += HOVER_DEGREES_PER_SECOND * deltaSeconds;
    renderLogo();
    hoverFrameId = requestAnimationFrame(hoverFrame);
  };

  const bindLogoMotion = (logo) => {
    activeLogo = logo;
    renderLogo();
    if (logo.dataset.adwoaMotionBound === 'true') return;
    logo.dataset.adwoaMotionBound = 'true';
    const fixedLink = logo.closest('.adwoa-fixed-logo-link');
    if (!fixedLink) return;

    let holdTimer = null;
    let touchPointer = null;
    let touchStart = null;
    let suppressClick = false;

    const startMotion = () => {
      hovering = true;
      lastHoverFrame = performance.now();
      if (!hoverFrameId) hoverFrameId = requestAnimationFrame(hoverFrame);
    };

    const stopMotion = () => {
      clearTimeout(holdTimer);
      holdTimer = null;
      touchPointer = null;
      touchStart = null;
      hovering = false;
      if (hoverFrameId) cancelAnimationFrame(hoverFrameId);
      hoverFrameId = null;
      lastHoverFrame = 0;
    };

    fixedLink.style.setProperty('-webkit-touch-callout', 'none');
    fixedLink.style.setProperty('user-select', 'none');
    fixedLink.style.setProperty('touch-action', 'pan-y');
    logo.style.setProperty('-webkit-touch-callout', 'none');

    fixedLink.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') startMotion();
    });
    fixedLink.addEventListener('pointerleave', stopMotion);
    fixedLink.addEventListener('pointerdown', (event) => {
      suppressClick = false;
      if (event.pointerType === 'mouse' || !event.isPrimary) return;
      stopMotion();
      touchPointer = event.pointerId;
      touchStart = { x: event.clientX, y: event.clientY };
      holdTimer = setTimeout(() => {
        holdTimer = null;
        suppressClick = true;
        startMotion();
      }, 180);
    });
    fixedLink.addEventListener('pointermove', (event) => {
      if (event.pointerId !== touchPointer || !touchStart) return;
      if (Math.hypot(event.clientX - touchStart.x, event.clientY - touchStart.y) > 10) {
        suppressClick = true;
        stopMotion();
      }
    }, { passive: true });
    window.addEventListener('pointerup', (event) => {
      if (event.pointerId === touchPointer) stopMotion();
    });
    window.addEventListener('pointercancel', stopMotion);
    window.addEventListener('blur', stopMotion);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopMotion();
    });
    fixedLink.addEventListener('contextmenu', (event) => {
      if (touchPointer !== null || suppressClick) event.preventDefault();
    });
    fixedLink.addEventListener('click', (event) => {
      if (suppressClick && event.detail !== 0) {
        event.preventDefault();
        suppressClick = false;
      }
    });
  };

  const setupAdwoaEditorialHeader = () => {
    const header = document.querySelector('.header');
    const originalLogo = header?.querySelector('img[src*="LOGO_bk_square.png"], .header__heading-link .adwoa-header-logo-svg');

    if (originalLogo) {
      originalLogo.src = ADWOA_LOGO;
      originalLogo.width = 104;
      originalLogo.height = 104;
      originalLogo.classList.add('adwoa-header-logo-svg');
      originalLogo.setAttribute('alt', 'ADWOA');
      header.classList.add('adwoa-header--inner');

      const originalLink = originalLogo.closest('.header__heading-link');
      let fixedLink = document.querySelector('.adwoa-fixed-logo-link');

      if (!fixedLink) {
        fixedLink = document.createElement('a');
        fixedLink.className = 'adwoa-fixed-logo-link';
        fixedLink.setAttribute('aria-label', 'ADWOA home');

        const fixedLogo = document.createElement('img');
        fixedLogo.className = 'adwoa-header-logo-svg';
        fixedLogo.src = ADWOA_LOGO;
        fixedLogo.alt = 'ADWOA';
        fixedLogo.width = 104;
        fixedLogo.height = 104;
        fixedLogo.draggable = false;

        fixedLink.appendChild(fixedLogo);
        document.body.appendChild(fixedLink);
      }

      fixedLink.href = originalLink?.href || '/';
      const fixedLogo = fixedLink.querySelector('.adwoa-header-logo-svg');
      if (fixedLogo) bindLogoMotion(fixedLogo);
    }

    const filters = document.querySelector('#main-collection-filters');
    const navList = document.querySelector('.desktop-header ul[role="list"]');

    if (filters && navList && !navList.querySelector('.adwoa-header-filter-item')) {
      const filterItem = document.createElement('li');
      filterItem.className = 'adwoa-header-filter-item';

      const filterButton = document.createElement('button');
      filterButton.type = 'button';
      filterButton.className = 'adwoa-header-filter-trigger';
      filterButton.textContent = 'FILTER';
      filterButton.setAttribute('aria-label', 'Filter products');
      filterButton.setAttribute('aria-haspopup', 'dialog');

      filterItem.appendChild(filterButton);

      const cartItem = navList.querySelector('#cart-icon-bubble')?.closest('li');
      navList.insertBefore(filterItem, cartItem || null);
      filters.classList.add('adwoa-filters-moved');

      filterButton.addEventListener('click', () => {
        const existingTrigger = filters.querySelector('summary.mobile-facets__open-wrapper');
        if (existingTrigger) existingTrigger.click();
      });
    }

    document.querySelectorAll('.product-grid-container .product-card-wrapper').forEach((card) => {
      if (card.dataset.adwoaProductClick === 'true') return;
      const productLink = card.querySelector('.card__heading a[href]');
      if (!productLink) return;

      card.dataset.adwoaProductClick = 'true';
      card.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.target.closest('a, button, input, select, textarea, label')) return;
        window.location.href = productLink.href;
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupAdwoaEditorialHeader, { once: true });
  } else {
    setupAdwoaEditorialHeader();
  }

  document.addEventListener('shopify:section:load', setupAdwoaEditorialHeader);
})();
